import { useState } from 'react'

import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import GradingIcon from '@mui/icons-material/Grading'
import InfoIcon from '@mui/icons-material/Info'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { EntityKpi } from '@components/EntityKpi'
import { PlanningsverschilIcon } from '@components/PlanningsverschilIcon'
import { StickyDeadlineIcon } from '@components/StickyDeadlineIcon'
import { useZipper } from '@hooks/useZipper'
import { formatDateTime, formatDateToISOWeek } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { AssignedBijlageCaption } from '../../../core/components/AssignedBijlageCaption'
import ExpandRow from '../../../theme/components/ExpandRow'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { AssignedBijlage as AssignedBijlageType } from '../types/AssignedBijlage'
import type { Bijlage } from '../types/Bijlage'

import { AssignedBijlageLog } from './AssignedBijlageLog'
import { BijlagenWithLogRows } from './BijlagenWithLogRows'

export { AssignedBijlage }

const headerCells = [
  { id: 'naam', label: 'Naam', fixed: 500 },
  { id: 'filler', label: '', fixed: 240 },
  { id: 'fille2', label: '', fixed: 240 },
  { id: 'created.date', label: 'Aangemaakt', fixed: 50 },
  { id: 'created.organisatie', label: 'Aangemaakt door', fixed: 150 },
  { id: 'filler3', label: '', fixed: 150 },
  { id: 'opties', label: '', fixed: 180 },
] as const

type Props = {
  assignedBijlage: AssignedBijlageType
  bijlagen: Bijlage[]
  user: AuthenticatedUser | Record<string, any>
  onOpen: (bijlage: Bijlage) => void
  onUpload: (bijlage: AssignedBijlageType) => void
  onCancel: (bijlage: Bijlage | AssignedBijlageType) => void
  onOpleveren: (bijlage: AssignedBijlageType) => void
  onReview: (bijlage: AssignedBijlageType) => void
  onEdit: (bijlage: AssignedBijlageType) => void
  showCancelAssignedBijlage: boolean
  isVerantwoordelijke: boolean
}

function AssignedBijlage({
  assignedBijlage,
  bijlagen,
  user,
  onOpen,
  onUpload,
  onCancel,
  onOpleveren,
  onReview,
  onEdit,
  showCancelAssignedBijlage,
  isVerantwoordelijke,
}: Props) {
  const {
    _id,
    documentsoort,
    discipline,
    assignedTo,
    dueWeek,
    completed,
    status,
    created,
    title,
    entity,
    herplanDiff,
    herplanReference,
    planningsverschil,
    netbeheerder,
    kpi,
  } = assignedBijlage
  const { organisatie } = user
  const isAssigned = status === 'assigned'
  const isOpgeleverd = status === 'done'
  const isUploaded = status === 'uploaded'
  const isCancelled = status === 'cancelled'
  const isWaiting = status === 'waiting'
  const isReview = status === 'review'
  const isCreator = created.organisatie === organisatie
  const assignedToOrganisatie = assignedTo === organisatie
  const hasBijlagen = bijlagen.length > 0
  const canEdit = (isAssigned || isUploaded) && !herplanDiff
  const isCompleted = !!completed?.date

  const [expandBijlagen, setExpandBijlagen] = useState(false)
  const [toggleLogDrawer, setToggleLogDrawer] = useState(false)

  const showCancelButton =
    showCancelAssignedBijlage &&
    (isVerantwoordelijke || assignedToOrganisatie || isCreator) &&
    !isReview &&
    !isCancelled

  const handleBijlagenClick = () => {
    setExpandBijlagen(!expandBijlagen)
  }

  const changeLogDrawer = () => setToggleLogDrawer(!toggleLogDrawer)

  let fileName = `${entity.id}-${entity.projecttype}-${assignedBijlage.processtatus}-${documentsoort}`
  if (discipline) {
    fileName = `${fileName}-${discipline}`
  }

  const { loading, save } = useZipper(fileName)

  return (
    <>
      <TableRow
        key={_id}
        sx={{
          backgroundColor: isCancelled ? 'lightgray !important' : '',
          m: 0,
        }}
      >
        <TableCell>
          <EntityKpi kpi={kpi} />
        </TableCell>

        <TableCell>
          <>
            {`${title ? `Overig: ${title}` : documentsoort} ${
              discipline || ''
            }`}

            <AssignedBijlageCaption assignedBijlage={assignedBijlage} />
          </>
        </TableCell>

        <TableCell>
          {assignedTo}
          {netbeheerder && assignedTo !== netbeheerder && (
            <Typography variant="caption">
              <br />
              {`namens ${netbeheerder}`}
            </Typography>
          )}
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasBijlagen ? (
              <ExpandRow
                data-testid="chipBijlagen"
                label={dueWeek}
                expanded={expandBijlagen}
                onDelete={handleBijlagenClick}
                onClick={handleBijlagenClick}
              />
            ) : (
              dueWeek
            )}

            {herplanReference && !isCompleted && (
              <StickyDeadlineIcon herplanReference={herplanReference} />
            )}

            {planningsverschil && (
              <PlanningsverschilIcon planningsverschil={planningsverschil} />
            )}
          </Box>
        </TableCell>

        <TableCell>{formatDateTime(created.date)}</TableCell>

        <TableCell>
          {created.organisatie}

          {created.email ? (
            <Typography variant="caption">
              <br />
              {created.email}
            </Typography>
          ) : (
            ''
          )}
        </TableCell>

        <TableCell>
          {isOpgeleverd && completed && (
            <Chip
              label={formatDateToISOWeek(completed.date)}
              variant="outlined"
              sx={{
                borderColor: 'green',
                color: 'green',
                backgroundColor: 'white',
              }}
            />
          )}
        </TableCell>

        <TableCell>
          {isVerantwoordelijke && canEdit && (
            <StyledIconButton
              title="Bijlage aanpassen"
              icon={<EditIcon />}
              onClick={() => onEdit(assignedBijlage)}
            />
          )}
          {(isVerantwoordelijke || assignedToOrganisatie) &&
            !(isCancelled || isOpgeleverd || isWaiting || isReview) && (
              <StyledIconButton
                title="Bijlagen uploaden"
                onClick={() => onUpload(assignedBijlage)}
                icon={<UploadFileRoundedIcon />}
              />
            )}

          {hasBijlagen && (
            <>
              {bijlagen.length === 1 && (
                <StyledIconButton
                  title="Bijlage openen"
                  icon={<CloudDownloadIcon />}
                  onClick={() => onOpen(bijlagen[0])}
                />
              )}

              {bijlagen.length > 1 &&
                bijlagen.find((bijlage) => bijlage.status !== 'cancelled') &&
                !isCancelled && (
                  <StyledIconButton
                    title="Bijlagen downloaden"
                    icon={
                      loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudDownloadIcon />
                      )
                    }
                    onClick={() =>
                      save(
                        bijlagen
                          .filter((bijlage) => bijlage.status !== 'cancelled')
                          .map(
                            (bijlage) =>
                              `/rest/${entity.type}/${entity.id}/bijlage/${bijlage._id}`,
                          ),
                      )
                    }
                  />
                )}

              {(isVerantwoordelijke || assignedToOrganisatie) && isUploaded && (
                <StyledIconButton
                  title="Bijlage opleveren"
                  icon={<PlaylistPlayIcon />}
                  onClick={() => onOpleveren(assignedBijlage)}
                />
              )}
              {(isVerantwoordelijke || assignedToOrganisatie) && isReview && (
                <StyledIconButton
                  title="Bijlage beoordelen"
                  icon={<GradingIcon />}
                  onClick={() => onReview(assignedBijlage)}
                />
              )}
            </>
          )}

          {showCancelButton && (
            <StyledIconButton
              title="Bijlage annuleren"
              icon={<DeleteIcon />}
              onClick={() => onCancel(assignedBijlage)}
            />
          )}

          <StyledIconButton
            title="Bijlage log"
            onClick={changeLogDrawer}
            icon={<InfoIcon />}
          />
        </TableCell>

        <AssignedBijlageLog
          open={toggleLogDrawer}
          onClose={changeLogDrawer}
          assignedBijlage={assignedBijlage}
        />
      </TableRow>

      {hasBijlagen && expandBijlagen ? (
        <TableRow>
          <TableCell colSpan={12} sx={{ padding: 0, paddingRight: '2px' }}>
            <Table
              sx={{
                backgroundColor: '#f7f7f7',
                filter: 'brightness(0.95)',
                border: '2px dashed rgba(0,0,0,.25)',
              }}
            >
              <TableHead>
                <TableRow>
                  {headerCells.map((headerCell) => (
                    <TableCell
                      key={headerCell.id}
                      sx={{
                        width: headerCell.fixed,
                      }}
                    >
                      {headerCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <BijlagenWithLogRows
                  assignedBijlage={assignedBijlage}
                  bijlagen={bijlagen}
                  onCancel={onCancel}
                  onOpen={onOpen}
                />
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}
